/**
 * One add-to-cart pipeline for every Nordstick surface that adds a line WITHOUT
 * going through the theme's own <product-form>. Two callers remain — the PDP
 * upsell in fa-main-product.js and the bundle widget in
 * ns-cart-bundle-value.js. Four more (the free-gift timer, the bundle-save
 * section, the cart quantity-bundle widget and the upsell popup) were deleted
 * as dead code in the same change that introduced this file.
 *
 * Each of those six surfaces used to carry its own copy of this sequence, and
 * its own identically-bodied POST helper under a different name
 * (postNewUpsellData / freeGiftAddToCart / postDataSB / postDataBundle /
 * postData). They drifted, and the drift was invisible: every one of them
 * dispatched `variant:add` as `this.dispatchEvent(...)` from inside a plain
 * function, so `this` was `window`, the event never reached the `document`
 * listener CartDrawer binds it on, and an add silently left the drawer shut.
 * A single owner for the sequence is what stops that recurring.
 *
 * Contract:
 * - Resolves with the fresh cart on success.
 * - REJECTS if the add or the follow-up cart read fails.
 * - Resolves with `null` when `items` is empty. An empty selection is not an
 *   error, so callers that act on success — navigating to checkout, say — must
 *   check for an empty selection BEFORE calling, or check for a null result.
 * - Callers own their own button state. This function deliberately does not
 *   touch the DOM of the surface that called it, so restoring a loader is the
 *   caller's job in both outcomes.
 */
(function () {
  if (window.nsCartAdd) return;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /*
   * Components that render from a cart section answer this by pushing their own
   * section id, so the add request can return their finished markup in the same
   * round trip. CartDrawer registers itself here; ns-cart-drawer-v2 pushes the
   * cart page's id by hand when the drawer is the surface being used.
   */
  function collectBundledSections(extra) {
    var sections = Array.isArray(extra) ? extra.slice() : [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sections },
      })
    );
    return sections;
  }

  /*
   * The old per-file helpers threw a bare "Network response was not ok", which
   * discarded the only useful part of a Shopify cart error — the `description`
   * telling you WHY (sold out, quantity unavailable, bad variant id). Keep it.
   */
  async function postJson(url, body) {
    var response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      var description = '';
      try {
        description = (await response.json()).description || '';
      } catch (parseError) {
        // A non-JSON body (a throttle page, an edge error) is still a failure;
        // the status alone carries enough to act on.
      }
      var error = new Error(
        'POST ' + url + ' responded ' + response.status + (description ? ': ' + description : '')
      );
      error.status = response.status;
      throw error;
    }

    return await response.json();
  }

  async function nsCartAdd(options) {
    var opts = options || {};
    var items = opts.items || [];
    if (!items.length) return null;

    var root = cartRoot();
    var sections = collectBundledSections(opts.sections);

    var added = await postJson(root + 'cart/add.js', { items: items, sections: sections });

    /*
     * cart/add.js returns only the added lines, so the totals, line list and
     * item count still have to come from the cart itself.
     */
    var cartResponse = await fetch(root + 'cart.js');
    if (!cartResponse.ok) {
      throw new Error('GET cart.js responded ' + cartResponse.status);
    }
    var cart = await cartResponse.json();
    cart.sections = added.sections;

    /*
     * variant:add MUST be dispatched on a node inside the document. CartDrawer
     * binds this on `document` and answers it with show() — that listener is
     * the only thing that opens the drawer on an add. Dispatched on `window`
     * (the old `this`) it never arrives, and the line is added invisibly.
     *
     * `detail.items` is the lines JUST ADDED, taken from the cart/add.js
     * response — the same shape the theme's own ProductForm sends. It is NOT
     * `cart.items`: ns-cart-drawer-v2 reads `items[0].product_id` to remember
     * the most recently added product, and `cart.items[0]` is the OLDEST line
     * in the cart, which would persist the wrong product id to localStorage and
     * pick the wrong follow-up upsell.
     */
    var addedItems = Object.prototype.hasOwnProperty.call(added, 'items')
      ? added.items
      : [added];

    document.documentElement.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: addedItems, cart: cart },
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cart },
      })
    );

    /*
     * Kept because it is a broadcast other surfaces depend on — the free-gift
     * timer repaints from it. The `document.querySelector('cart-drawer')
     * ._onCartRefresh()` call that used to follow it in all six copies is gone:
     * CartDrawer already listens for cart:refresh, so the explicit call was a
     * second fetch of the same section, and it reached into another component's
     * private method with no null check, throwing on any page with no drawer.
     */
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    return cart;
  }

  window.nsCartAdd = nsCartAdd;
})();
